import type { KeyAccessor } from '@/types/Helpers/Arrays'
import type { Ref } from 'vue'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { QueryParams } from '@/const/ShipmentsListQueryFilters'
import { appendToParams } from '@/services/Url'
import { ProxyMode } from '@/types/Components/SearchForm'

const transformStatuses = (input: string) => {
  if (!input) {
    return null
  }

  const map: KeyAccessor = {
    out_for_delivery: ['in_transit', 'out_for_delivery'],
    delivered: ['delivered'],
    delivery_issue: ['exception', 'failed_attempt'],
    returned: ['returned'],
  }

  return input
    .split(',')
    .map((status: string) => map[status] || [status])
    .flatMap((status: string) => status)
}

type Input = {
  route: RouteLocationNormalizedLoaded
  serverOptions: Ref
}

export const makeRequestParams = ({ route, serverOptions }: Input) => {
  const sortBy: string =
    {
      receivedInFacilityAt: 'received_in_facility_at',
      lastTrackingEventTime: 'last_tracking_event_time',
      createdAt: 'created_at',
    }[String(serverOptions.value.sortBy)] || 'created_at'
  const params = new URLSearchParams({
    limit: String(serverOptions.value.rowsPerPage || 50),
    page: String(serverOptions.value.page || 1),
    ascending: String(serverOptions.value.sortType === 'asc' ? 1 : 0),
    orderBy: sortBy,
  })

  const lastTrackingStatus = transformStatuses(String(route?.query.statuses))
  if (route?.query.statuses && lastTrackingStatus) {
    const appender = appendToParams(params, QueryParams.LAST_TRACKING_STATUS)
    lastTrackingStatus.forEach(appender)
  }

  if (route?.query.carriers) {
    const appender = appendToParams(params, QueryParams.CARRIER_HANDLE)
    String(route.query.carriers).split(',').forEach(appender)
  }

  if (route?.query.shipmentNumber) {
    const appender = appendToParams(params, QueryParams.SHIPMENT_NUMBER)
    String(route.query.shipmentNumber).split(',').forEach(appender)
  }

  if (route?.query.country) {
    const appender = appendToParams(params, QueryParams.COUNTRY)
    String(route.query.country).split(',').forEach(appender)
  }

  if (route?.query.email) {
    const appender = appendToParams(params, QueryParams.EMAIL)
    String(route.query.email).split(',').forEach(appender)
  }

  if (route?.query.phone) {
    const appender = appendToParams(params, QueryParams.PHONE)
    String(route.query.phone).split(',').forEach(appender)
  }

  if (route?.query.customer) {
    const appender = appendToParams(params, QueryParams.CUSTOMER)
    String(route.query.customer).split(',').forEach(appender)
  }

  if (route?.query.assignment) {
    const appender = appendToParams(params, QueryParams.ASSIGNMENT)
    String(route.query.assignment).split(',').forEach(appender)
  }

  if (route?.query.distributionChannel) {
    const appender = appendToParams(params, QueryParams.DISTRIBUTION_CHANNEL)
    String(route.query.distributionChannel).split(',').forEach(appender)
  }

  if (route?.query.query) {
    params.append(QueryParams.QUERY, String(route.query.query))
  }

  if (route?.query.dateFrom) {
    params.append(QueryParams.DATE_FROM, String(route.query.dateFrom))
  }

  if (route?.query.dateTo) {
    params.append(QueryParams.DATE_TO, String(route.query.dateTo))
  }

  if (route?.query.packagesDivertedToServicePoint) {
    params.append(QueryParams.PACKAGES_DIVERTED_TO_SERVICE_POINT, 'true')
  }

  if (route?.query.withServicePointCollectingProblem) {
    params.append(QueryParams.WITH_SERVICE_POINT_COLLECTING_PROBLEM, 'true')
  }

  if (route?.query.withRecipientNotificationProblem) {
    params.append(QueryParams.WITH_RECIPIENT_NOTIFICATION_PROBLEM, 'true')
  }

  if (route?.query.checkedIn) {
    params.append(QueryParams.CHECKED_IN, 'true')
  }

  if (route?.query.returnAssignments) {
    params.append(QueryParams.RETURN_ASSIGNMENTS, 'true')
  }

  const resolveShouldUseProxy = (): boolean | null => {
    const map: Record<string, boolean> = {
      [ProxyMode.WITH]: true,
      [ProxyMode.WITHOUT]: false,
    }

    if (!route?.query.proxyMode) {
      return null
    }

    return map[route.query.proxyMode.toString()] ?? null
  }

  const shouldUseProxy = resolveShouldUseProxy()
  if (shouldUseProxy !== null) {
    params.append(QueryParams.SHOULD_USE_PROXY, String(shouldUseProxy))
  }

  if (route?.query.orderBy) {
    params.append(QueryParams.ORDER_BY, String(route.query.orderBy))
  }

  if (route?.query.ascending) {
    params.append(QueryParams.ASCENDING, 'true')
  }

  return params
}
